import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import { DefaultRoleTypesEnum, getDefaultRoleTranslation } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import getEnabledPermissionCount from 'utils/permissions/getEnabledPermissionCount';

import RoleListItem from './RoleListItem';

interface RolesListProps {
  onItemClick: (id: string) => void;
}

// Owner first, then Member — fixed order per AC-041a / Decision 10.
const DEFAULT_ROLE_PRIORITY: Record<DefaultRoleTypesEnum, number> = {
  [DefaultRoleTypesEnum.OWNER]: 0,
  [DefaultRoleTypesEnum.MEMBER]: 1,
};

const sortDefaults = (roles: WorkspaceRoleV2[]): WorkspaceRoleV2[] =>
  [...roles].sort((a, b) => {
    const aPriority = a.defaultRoleType ? DEFAULT_ROLE_PRIORITY[a.defaultRoleType] : Number.MAX_SAFE_INTEGER;
    const bPriority = b.defaultRoleType ? DEFAULT_ROLE_PRIORITY[b.defaultRoleType] : Number.MAX_SAFE_INTEGER;
    return aPriority - bPriority;
  });

// Same comparator for both inherited and custom sections per AC-041a — a
// locale-aware, case-insensitive collation lifted to module scope so the
// `Intl.Collator` instance can be re-used per render.
const sortByNameLocaleAware = (roles: WorkspaceRoleV2[], language: string): WorkspaceRoleV2[] => {
  const collator = new Intl.Collator(language, { sensitivity: 'base' });
  return [...roles].sort((a, b) => collator.compare(a.name, b.name));
};

const RolesList = ({ onItemClick }: RolesListProps) => {
  const { i18n } = useTranslation();
  const { workspaceRoles } = useWorkspacesContext({ requireWorkspaceId: true });

  // Partition into the three buckets defined in tech.md §3.8 + AC-041:
  //   1. defaults (Owner → Member)
  //   2. inherited (sub-workspace only — bot-propagated, locale-sorted)
  //   3. custom (everything else — locale-sorted)
  // No section header rows or dividers per ux.md §1.3 — the badges on each
  // row are the only cue separating buckets.
  const orderedRoles = useMemo(() => {
    const defaults = workspaceRoles.filter((role) => !!role.defaultRoleType);
    const inherited = workspaceRoles.filter((role) => !role.defaultRoleType && role.inherited === true);
    const custom = workspaceRoles.filter((role) => !role.defaultRoleType && !role.inherited);

    return [
      ...sortDefaults(defaults),
      ...sortByNameLocaleAware(inherited, i18n.language),
      ...sortByNameLocaleAware(custom, i18n.language),
    ];
  }, [workspaceRoles, i18n.language]);

  // AC-046: no V1 empty placeholder branch — defaults always exist, so the
  // list is never literally empty. If the upstream state arrives in an
  // unexpected empty shape the surrounding card simply renders no rows.
  return (
    <div className="flex flex-col">
      {orderedRoles.map((role) => {
        const roleName = getDefaultRoleTranslation(role.defaultRoleType, role.name) || role.name;
        const permissionsCount = getEnabledPermissionCount(role);
        const setRole = () => onItemClick(role.id);

        return (
          <RoleListItem
            key={role.id}
            name={roleName}
            permissionsCount={permissionsCount}
            onClick={setRole}
            isDefaultRole={!!role.defaultRoleType}
            isInheritedRole={role.inherited === true}
          />
        );
      })}
    </div>
  );
};

export default RolesList;
